import { type FC, type PropsWithChildren, useEffect, useState } from 'react';

import classNames from 'classnames';

import { getUserLabel } from '@iconik-io/core/src/get_user_label';
import { FLAG_SIMPLIFIED_ACLS } from '@iconik-io/shared-lib/src/analytics/flags';
import { growthbook } from '@iconik-io/shared-lib/src/analytics/growthbook';
import { FIELD_TYPES } from '@iconik-io/shared-lib/src/constants/search';
import { useEntityBatch } from '@iconik-io/shared-lib/src/react/hooks/entity/use_entity_batch';

import AsyncUser from '../../../components/async_user/async_user';
import DebugDump from '../../../components/debug_dump/debug_dump';
import AclsApplyToContentModal from '../../../components/modals/acls_apply_to_content/acls_apply_to_content';
import AclsDeleteFromContentModal from '../../../components/modals/acls_delete_from_content/acls_delete_from_content';
import ModalsManager from '../../../components/modals/modals_manager/modals_manager';
import { NoContentPanelCompact } from '../../../components/no_content_panel_compact/no_content_panel_compact';
import UserItem from '../../../components/user_item/user_item';
import { EVERYONE_GROUP_ID } from '../../../constants/groups';
import { ACLS_HELP } from '../../../constants/help_urls';
import {
  ACLS_APPLY_TO_CONTENT,
  ACLS_DELETE_FROM_CONTENT,
} from '../../../constants/modals';
import {
  ACL_TYPE_INHERITED,
  ACL_TYPE_PROPAGATED,
} from '../../../ducks/common_acls/constants';
import { aclsSetEditByType } from '../../../ducks/common_acls/thunks';
import type {
  CommonAclsPrefix,
  EntityGroupAcl,
  EntityUserAcl,
} from '../../../ducks/common_acls/types';
import { useAppDispatch } from '../../../store/react_redux_typed';
import Button from '../../../ui/button/button';
import Label from '../../../ui/label/label';
import LoadingBar from '../../../ui/loading_bar/loading_bar';
import type { SelectOption } from '../../../ui/select_field/types';
import type { Column } from '../../../ui/table/types';
import scrollTo from '../../../utils/scroll_to';
import AclsForm, { type AclsFormData } from '../acls_form/acls_form';
import AclsTable from '../acls_table/acls_table';
import type AclsListContextMenu from '../context_menu';
import Permissions from '../permissions/permissions';
import type { EntityGroupAclTableItem, EntityUserAclTableItem } from '../types';

import styles from './acls_controls.module.css';

type Props = PropsWithChildren<{
  visibleAcl: boolean;
  writeAcl: boolean;
  deleteAcl: boolean;
  defaultExpanded?: boolean;
  usersElementAcls: EntityUserAcl[];
  groupsElementAcls: EntityGroupAcl[];
  validAcls: (data: AclsFormData) => void;
  onChange: (data: AclsFormData) => void;
  aclsObjectId?: string;
  aclsType?: CommonAclsPrefix;
  edit?: string;
  stickyForm?: boolean;
  isModal?: boolean;
  loading?: boolean;
  contextMenu?: typeof AclsListContextMenu;
  useEmptyState?: boolean;
  additionalTeamOptions?: SelectOption[];
}>;

const InheritanceCell = ({
  item,
}: {
  item: EntityUserAclTableItem | EntityGroupAclTableItem;
}) => {
  if (item.type === ACL_TYPE_INHERITED) {
    return (
      <div className={styles.inheritance_column}>
        <Label variant="warning">Inherited</Label>
      </div>
    );
  }
  if (item.type === ACL_TYPE_PROPAGATED) {
    return (
      <div className={styles.inheritance_column}>
        <Label variant="success">Propagates</Label>
      </div>
    );
  }
  return null;
};

// Built per table because a Column is invariant in its item type, but the cell
// itself is a module constant so rows are not remounted on every render.
const inheritanceColumn = <
  Item extends EntityUserAclTableItem | EntityGroupAclTableItem,
>(): Column<Item> => ({
  name: 'type',
  type: FIELD_TYPES.CUSTOM_COMPONENT,
  component: InheritanceCell,
});

export const usersColumns = (props: {
  edit?: string;
}): Column<EntityUserAclTableItem>[] => {
  const columns: Column<EntityUserAclTableItem>[] = [];
  columns.push({
    name: 'first_name',
    type: FIELD_TYPES.CUSTOM_COMPONENT,
    component: ({ item }) => (
      <>
        <DebugDump label="ACL" data={item} />
        <AsyncUser nowrap id={item.user_id}>
          {({ user }) => (
            <UserItem
              wrap
              fullName={getUserLabel(user)}
              photo={user.photo_small}
            />
          )}
        </AsyncUser>
      </>
    ),
    style: {
      maxWidth: 0,
      width: '50%',
    },
  });

  columns.push(inheritanceColumn<EntityUserAclTableItem>());

  columns.push({
    name: 'permissions',
    type: FIELD_TYPES.CUSTOM_COMPONENT,
    component: ({ item }) => (
      <Permissions acl={item} edit={props.edit === item.id} />
    ),
    style: {
      width: '50%',
    },
  });

  columns.push({
    label: ' ',
    name: 'user_id',
    type: FIELD_TYPES.MENU,
    style: {
      width: '25px',
    },
  });

  return columns;
};

export const groupsColumns = (props: {
  edit?: string;
}): Column<EntityGroupAclTableItem>[] => {
  const showSimplifiedAcls = growthbook?.isOn(FLAG_SIMPLIFIED_ACLS) ?? false;
  const columns: Column<EntityGroupAclTableItem>[] = [];
  columns.push({
    name: 'name',
    type: FIELD_TYPES.CUSTOM_COMPONENT,
    component: ({ item }) => {
      const entityType =
        item.group_id === EVERYONE_GROUP_ID || !showSimplifiedAcls
          ? 'groups_basic'
          : 'teams_basic';

      const { entity } = useEntityBatch({
        entityType,
        primaryId: item.group_id,
      });

      if (!entity) {
        return;
      }

      return (
        <>
          <DebugDump label="ACL" data={item} />
          <UserItem
            wrap
            fullName={entity?.name?.trim()}
            annotation={entity?.is_legacy_everyone ? '(Legacy)' : ''}
          />
        </>
      );
    },
    style: {
      maxWidth: 0,
      width: '50%',
    },
  });

  columns.push(inheritanceColumn<EntityGroupAclTableItem>());

  columns.push({
    name: 'permissions',
    type: FIELD_TYPES.CUSTOM_COMPONENT,
    component: ({ item }) => (
      <Permissions acl={item} edit={props.edit === item.id} />
    ),
    style: {
      width: '50%',
    },
  });

  columns.push({
    label: ' ',
    name: 'group_id',
    type: FIELD_TYPES.MENU,
    style: {
      width: '25px',
    },
  });

  return columns;
};

const AclsControls: FC<Props> = (props) => {
  const [open, setOpen] = useState(props.defaultExpanded ?? false);
  const showSimplifiedAcls = growthbook?.isOn(FLAG_SIMPLIFIED_ACLS) ?? false;
  const dispatch = useAppDispatch();
  // biome-ignore lint/correctness/useExhaustiveDependencies: unmount
  useEffect(
    () => () => {
      if ('aclsType' in props && props.aclsType) {
        dispatch(aclsSetEditByType(props.aclsType, undefined));
      }
    },
    [],
  );
  useEffect(() => {
    if (open) {
      const objDiv = document.getElementById('panel_acls');
      if (objDiv) {
        scrollTo(objDiv, objDiv.scrollHeight, 500);
      }
    }
  }, [open]);

  const handleClick = () => {
    setOpen(true);
  };

  const {
    visibleAcl,
    validAcls,
    children,
    onChange,
    writeAcl,
    aclsType,
    aclsObjectId,
    stickyForm,
    isModal,
    usersElementAcls,
    groupsElementAcls,
    useEmptyState,
    additionalTeamOptions,
  } = props;

  if (!visibleAcl) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <ModalsManager
        modals={{
          [ACLS_APPLY_TO_CONTENT]: AclsApplyToContentModal,
          [ACLS_DELETE_FROM_CONTENT]: AclsDeleteFromContentModal,
        }}
      />
      {writeAcl ? (
        <div className={classNames({ [styles.form_sticky]: stickyForm })}>
          {!open ? (
            <div className={styles.button_container}>
              <Button iconName="plus" onClick={handleClick}>
                Add access
              </Button>
            </div>
          ) : (
            <div className={styles.form_container}>
              <div className={styles.form_header}>
                <div className={styles.title}>Add new</div>
              </div>
              <AclsForm
                isModal={isModal}
                onValidAclFormSubmit={validAcls}
                onChange={onChange}
                additionalTeamOptions={additionalTeamOptions}
              >
                {children}
              </AclsForm>
            </div>
          )}
        </div>
      ) : undefined}
      {props.loading && <LoadingBar className={styles.loading} />}
      <AclsTable<EntityUserAclTableItem>
        contextMenu={props.contextMenu}
        columns={usersColumns(props)}
        items={usersElementAcls.map((item) => ({
          ...item,
          id: `${item.type}_${item.objectKey}_${item.user_id}`,
          aclsType,
          aclsObjectId,
        }))}
        title={
          <div className={styles.table_header}>
            <div className={styles.title}>Users</div>
          </div>
        }
      />
      <AclsTable<EntityGroupAclTableItem>
        contextMenu={props.contextMenu}
        columns={groupsColumns(props)}
        items={groupsElementAcls.map((item) => ({
          ...item,
          id: `${item.type}_${item.objectKey}_${item.group_id}`,
          aclsType,
          aclsObjectId,
        }))}
        title={
          <div className={styles.table_header}>
            <div className={styles.title}>
              {showSimplifiedAcls ? 'Teams' : 'Groups'}
            </div>
          </div>
        }
      />
      {useEmptyState &&
      usersElementAcls.length <= 1 &&
      groupsElementAcls.length <= 1 &&
      !props.loading ? (
        <NoContentPanelCompact
          title={`Control access to this content by adding, removing, or restricting users and ${showSimplifiedAcls ? 'teams' : 'groups'}`}
          linkText="Learn more"
          linkHref={ACLS_HELP}
        />
      ) : null}
    </div>
  );
};

export default AclsControls;
